// Copyright 2025-2026 Florian Gallon. All Rights reserved.
/**
 * Stripe Utility Functions for Frontend
 *
 * Helper functions for Stripe integration in React components
 */

import { loadStripe } from '@stripe/stripe-js';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { auth } from '../firebase';
import { getApp } from 'firebase/app';

/**
 * Get Firebase Functions instance with current auth context
 */
const getFunctionsInstance = () => {
  const app = getApp();
  return getFunctions(app, 'us-central1');
};

/**
 * Get Stripe publishable key based on environment
 */
const getStripePublishableKey = () => {
  // Use environment variable or fallback to test key
  return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
};

/**
 * Initialize Stripe instance (singleton pattern)
 */
let stripePromise = null;

export const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = getStripePublishableKey();
    if (!publishableKey) {
      console.error('Stripe publishable key not configured');
      return null;
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

/**
 * Create a Stripe Checkout Session for subscription
 * @param {string} bakeryId - Bakery ID
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment is canceled
 * @returns {Promise<Object>} Session data with URL
 */
export const createCheckoutSession = async (bakeryId, successUrl, cancelUrl) => {
  try {
    // Get fresh Functions instance with current auth context
    const functions = getFunctionsInstance();

    // Verify user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be logged in to create checkout session');
    }

    const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckoutSessionFn({
      bakeryId,
      successUrl,
      cancelUrl
    });

    return result.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create a Stripe Customer Portal Session
 * @param {string} bakeryId - Bakery ID
 * @param {string} returnUrl - URL to return to after portal session
 * @returns {Promise<Object>} Portal session data with URL
 */
export const createPortalSession = async (bakeryId, returnUrl) => {
  try {
    console.log('[stripeUtils] Creating portal session...');

    // Get fresh Functions instance with current auth context
    const functions = getFunctionsInstance();
    console.log('[stripeUtils] Functions object:', functions);
    console.log('[stripeUtils] Functions region:', functions.region);
    console.log('[stripeUtils] Functions app:', functions.app?.options?.projectId);

    // Verify user is authenticated
    const currentUser = auth.currentUser;
    console.log('[stripeUtils] Current user:', currentUser?.uid);
    if (!currentUser) {
      throw new Error('User must be logged in to access billing portal');
    }

    const createPortalSessionFn = httpsCallable(functions, 'createPortalSession');
    console.log('[stripeUtils] Callable function created');

    const result = await createPortalSessionFn({
      bakeryId,
      returnUrl
    });
    console.log('[stripeUtils] Result received:', result);

    return result.data;
  } catch (error) {
    console.error('[stripeUtils] Error creating portal session:', error);
    console.error('[stripeUtils] Error code:', error.code);
    console.error('[stripeUtils] Error message:', error.message);
    console.error('[stripeUtils] Error details:', error.details);
    throw error;
  }
};

/**
 * Cancel subscription (at period end)
 * @param {string} bakeryId - Bakery ID
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async (bakeryId) => {
  try {
    // Get fresh Functions instance with current auth context
    const functions = getFunctionsInstance();

    // Verify user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be logged in to cancel subscription');
    }

    const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');
    const result = await cancelSubscriptionFn({
      bakeryId
    });

    return result.data;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Format currency amount
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (USD, EUR, CAD)
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount, currency) => {
  const amountInDollars = amount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amountInDollars);
};

/**
 * Get subscription price based on country
 * @param {string} country - Country code (US, FR, CA)
 * @returns {Object} Price information
 */
export const getSubscriptionPrice = (country) => {
  const prices = {
    'US': { amount: 20, currency: 'USD', symbol: '$' },
    'FR': { amount: 20, currency: 'EUR', symbol: '€' },
    'CA': { amount: 25, currency: 'CAD', symbol: 'CA$' },
    'GB': { amount: 15, currency: 'GBP', symbol: '£' }
  };

  return prices[country] || prices['US'];
};

/**
 * Check if bakery is grandfathered (existing baker before subscription launch)
 * @param {Object} bakeryData - Bakery document data
 * @returns {boolean}
 */
export const isGrandfathered = (bakeryData) => {
  return bakeryData?.isGrandfathered === true;
};

/**
 * Check if subscription is active
 * @param {Object} bakeryData - Bakery document data
 * @returns {boolean}
 */
export const hasActiveSubscription = (bakeryData) => {
  // Grandfathered bakers always have access
  if (isGrandfathered(bakeryData)) {
    return true;
  }

  // Check subscription status - 'active' and 'trialing' are considered active subscriptions
  // 'grace_period' is NOT an active subscription - it's a temporary free period
  const status = bakeryData?.subscriptionStatus;
  if (status === 'active' || status === 'trialing') {
    return true;
  }

  // No active subscription (includes 'grace_period', 'canceled', 'past_due', etc.)
  return false;
};

/**
 * Get subscription status display information
 * @param {string} status - Subscription status
 * @returns {Object} Display information
 */
export const getSubscriptionStatusDisplay = (status) => {
  const statusMap = {
    'active': {
      labelKey: 'statusActive',
      color: 'green',
      description: 'Your subscription is active'
    },
    'trialing': {
      labelKey: 'statusTrialing',
      color: 'blue',
      description: 'You are in your free trial period'
    },
    'past_due': {
      labelKey: 'statusPastDue',
      color: 'orange',
      description: 'Payment failed - please update your payment method'
    },
    'canceled': {
      labelKey: 'statusCanceled',
      color: 'red',
      description: 'Your subscription has been canceled'
    },
    'incomplete': {
      labelKey: 'statusIncomplete',
      color: 'yellow',
      description: 'Subscription setup incomplete'
    },
    'none': {
      labelKey: 'statusNone',
      color: 'gray',
      description: 'No active subscription'
    }
  };

  return statusMap[status] || statusMap['none'];
};

/**
 * Calculate days remaining in trial
 * @param {Date} trialEnd - Trial end date
 * @returns {number} Days remaining
 */
export const getDaysRemainingInTrial = (trialEnd) => {
  if (!trialEnd) return 0;

  const now = new Date();
  const end = new Date(trialEnd);
  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Get grace period information
 * @param {Object} bakeryData - Bakery document data
 * @returns {Object} Grace period info { isInGracePeriod, daysRemaining, endDate }
 */
export const getGracePeriodInfo = (bakeryData) => {
  if (!bakeryData?.createdAt) {
    return { isInGracePeriod: false, daysRemaining: 0, endDate: null };
  }

  const createdDate = bakeryData.createdAt.toDate?.() || new Date(bakeryData.createdAt.seconds * 1000);
  const gracePeriodEnd = new Date(createdDate);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 7);

  // Normalize to start of day in UTC (match backend logic)
  const now = new Date();
  const todayStartUTC = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    0, 0, 0, 0
  ));

  const endDateStartUTC = new Date(Date.UTC(
    gracePeriodEnd.getUTCFullYear(),
    gracePeriodEnd.getUTCMonth(),
    gracePeriodEnd.getUTCDate(),
    0, 0, 0, 0
  ));

  const isInGracePeriod = now < endDateStartUTC;

  if (!isInGracePeriod) {
    return { isInGracePeriod: false, daysRemaining: 0, endDate: gracePeriodEnd };
  }

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.floor((endDateStartUTC - todayStartUTC) / msPerDay);

  return {
    isInGracePeriod: true,
    daysRemaining: Math.max(0, daysRemaining),
    endDate: gracePeriodEnd
  };
};

/**
 * Format card brand for display
 * @param {string} brand - Card brand
 * @returns {string} Formatted brand name
 */
export const formatCardBrand = (brand) => {
  const brandMap = {
    'visa': 'Visa',
    'mastercard': 'Mastercard',
    'amex': 'American Express',
    'discover': 'Discover',
    'diners': 'Diners Club',
    'jcb': 'JCB',
    'unionpay': 'UnionPay'
  };

  return brandMap[brand?.toLowerCase()] || brand;
};

/**
 * Handle Stripe error and return user-friendly message
 * @param {Error} error - Stripe error object
 * @returns {string} User-friendly error message
 */
export const handleStripeError = (error) => {
  if (!error) return 'An unknown error occurred';

  // Firebase Functions errors
  if (error.code) {
    switch (error.code) {
      case 'unauthenticated':
        return 'Please log in to continue';
      case 'permission-denied':
        return 'You do not have permission to perform this action';
      case 'not-found':
        return 'Subscription not found';
      case 'failed-precondition':
        return error.message || 'Unable to proceed with this action';
      default:
        return error.message || 'An error occurred';
    }
  }

  // Stripe API errors
  if (error.type) {
    switch (error.type) {
      case 'card_error':
        return error.message || 'Your card was declined';
      case 'validation_error':
        return 'Please check your information and try again';
      case 'api_error':
        return 'Payment processing error. Please try again';
      case 'authentication_error':
        return 'Authentication error. Please try again';
      case 'rate_limit_error':
        return 'Too many requests. Please try again later';
      default:
        return error.message || 'Payment error occurred';
    }
  }

  return error.message || 'An error occurred';
};
